import { useMemo } from 'react';

import { type DocNode } from '@atlaskit/adf-schema';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { useChatMessages } from '@atlassian/conversation-assistant-store/controllers/chat/use-chat-messages';

import { findLastAssistantMessage } from '../../common/utils';
import type { SmartCreationChatStoreApi } from '../../smart-creation-chat-store-api';

type UseSelectedPreviewProps = {
	useSmartCreationChatStoreApi: () => SmartCreationChatStoreApi;
};

export const useLastAssistantMessageAuthLink = ({
	useSmartCreationChatStoreApi,
}: UseSelectedPreviewProps): string | null => {
	const { activeConversation } = useSmartCreationChatStoreApi();
	const [chatMessages] = useChatMessages({ conversationId: activeConversation?.id });

	return useMemo(() => {
		if (!fg('aifc_show_connected_apps_auth_login_screen')) {
			return null;
		}

		if (chatMessages.length === 0) {
			return null;
		}

		const lastAssistantMessage = findLastAssistantMessage(chatMessages);
		if (!lastAssistantMessage) {
			return null;
		}

		return extractOutboundAuthLink(lastAssistantMessage?.content?.message?.message?.content ?? '');
	}, [chatMessages]);
};

export function extractOutboundAuthLink(content: string | DocNode): string | null {
	if (!content) {
		return null;
	}

	let textContent: string;

	if (typeof content === 'object') {
		textContent = JSON.stringify(content);
	} else {
		textContent = content;
	}

	const regex = /https:\/\/id\.(stg\.internal\.)?atlassian\.com\/outboundAuth\/start[^\s)"]*/g;
	const matches = textContent.match(regex);

	return matches ? matches[0] : null;
}
